import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { FaEnvelope, FaLock, FaUser } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ClipLoader } from 'react-spinners'
import { Redirect } from 'react-router-dom'
import { useDispatch } from 'store'
import { ActionType, Selectors } from 'Reducers'
import { authErrorKey, checkIfIsEmbeddedBrowser, isValidEmail, MAX_NAME_LENGTH, validateEmail, validateName, validatePassword } from 'Lib'
import IconInput from 'Components/IconInput'
import Button from 'Components/Button'
import OrLineDivider from 'Components/OrLineDivider'
import ResetPasswordModal from 'Components/Login/ResetPassword'
import SocialLogin from 'Components/Login/SocialLogin'
import UscHeader from 'Components/UscHeader'
import { showToast } from 'Components/Toast'
import { UscSocialProvider } from 'Reducers/UscRedux'
import { RouteNames } from 'RouteNames'
import { theme } from 'styles/theme'
import {
  ActionBar,
  Card,
  ErrorText,
  Form,
  FormRow,
  Notice,
  Page,
  Progress,
  ProgressFill,
  StepLabel,
  Title,
  USC_TOTAL_STEPS
} from 'Containers/UscShared'

type AccountMode = 'register' | 'signIn'

const STEP = 2

const Tabs = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.grey50};
  border: 1px solid ${({ theme }) => theme.colors.grey100};
  border-radius: 14px;
  padding: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.mediumLarge};
`

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  border: none;
  cursor: pointer;
  min-height: 44px;
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: 10px;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  background-color: ${({ active, theme }) => (active ? theme.colors.primary : 'transparent')};
  color: ${({ active, theme }) => (active ? theme.colors.basicWhite : theme.colors.text)};
  box-shadow: ${({ active }) => (active ? '0 2px 8px rgba(3, 48, 44, 0.15)' : 'none')};
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
`

const ForgotPassword = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  text-decoration: underline;
`

/**
 * Step 2 of the Urban Sports Club voucher flow: the member registers or signs in.
 * The code is redeemed straight afterwards by the saga (step 3), which is why the
 * button reads "Redeem & activate" rather than "Sign in".
 *
 * The password is intentionally kept in component state only — never dispatched
 * into the Redux store beyond the action that consumes it.
 */
const UscAccountContainer = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const validatedCode = useSelector(Selectors.getUscValidatedCode)

  const [mode, setMode] = useState<AccountMode>('register')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [emailBlurred, setEmailBlurred] = useState(false)
  const [passwordBlurred, setPasswordBlurred] = useState(false)
  const [nameBlurred, setNameBlurred] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)

  // Social sign-in runs in a popup, which these browsers either block or open in a
  // window the member cannot get back from — same gate as the login screen.
  const socialLoginAvailable = useMemo(() => !checkIfIsEmbeddedBrowser() && mode === 'signIn', [mode])

  useEffect(() => {
    logEvent('web_usc_accountScreenViewed')
  }, [])

  // Reached only from the code screen — a direct hit has nothing to redeem.
  if (!validatedCode) {
    return <Redirect to={RouteNames.USC_REDEEM} />
  }

  const trimmedEmail = email.trim()
  const trimmedPassword = password.trim()
  const trimmedName = name.trim()

  const emailError = validateEmail(trimmedEmail)
  const passwordError = validatePassword(trimmedPassword)
  const nameError = validateName(trimmedName)

  const isRegistering = mode === 'register'
  const credentialsValid = isValidEmail(trimmedEmail) && !passwordError
  const isFormValid = isRegistering ? credentialsValid && !nameError : credentialsValid

  const handleFailure = (err: Error) => {
    setLoading(false)
    setError(t(authErrorKey(err))!)
  }

  const switchMode = (next: AccountMode) => {
    setMode(next)
    setError('')
    logEvent('web_usc_accountModeSwitch', { mode: next })
  }

  /** Google and Apple cover both cases at once: the account is created if it is new. */
  const handleSocialSignIn = (provider: UscSocialProvider) => {
    if (loading) {
      return
    }

    setError('')
    setLoading(true)
    logEvent('web_usc_socialSignInPressed', { provider })

    dispatch({
      type: ActionType.USC_SOCIAL_SIGN_IN_AND_REDEEM,
      provider,
      reject: handleFailure,
      // Popup closed — nothing went wrong, so release the screen without an error.
      onCancel: () => setLoading(false)
    })
  }

  const handleResetPassword = (resetEmail: string) => {
    dispatch({
      type: ActionType.RESET_PASSWORD_REQUEST,
      email: resetEmail,
      onError: (errorMessage: string) => showToast('error', errorMessage),
      onSuccess: () => showToast('success', t('resetPasswordModal.successMessage'))
    })
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (loading) {
      return
    }

    setError('')
    setLoading(true)

    if (isRegistering) {
      dispatch({
        type: ActionType.USC_REGISTER_AND_REDEEM,
        name: trimmedName,
        email: trimmedEmail,
        password: trimmedPassword,
        reject: handleFailure
      })
    } else {
      dispatch({
        type: ActionType.USC_SIGN_IN_AND_REDEEM,
        email: trimmedEmail,
        password: trimmedPassword,
        reject: handleFailure
      })
    }
  }

  return (
    <Page>
      <UscHeader />
      <Card>
        <StepLabel>{t('usc.step', { current: STEP, total: USC_TOTAL_STEPS })}</StepLabel>
        <Progress>
          <ProgressFill step={STEP} />
        </Progress>
        <Title>{t('usc.account.title')}</Title>

        <Tabs role='tablist'>
          <Tab
            type='button'
            role='tab'
            aria-selected={isRegistering}
            active={isRegistering}
            onClick={() => switchMode('register')}
          >
            {t('usc.account.tabRegister')}
          </Tab>
          <Tab
            type='button'
            role='tab'
            aria-selected={!isRegistering}
            active={!isRegistering}
            onClick={() => switchMode('signIn')}
          >
            {t('usc.account.tabSignIn')}
          </Tab>
        </Tabs>

        <Form onSubmit={handleSubmit} aria-busy={loading}>
          <FormRow>
            <IconInput
              icon={<FaEnvelope />}
              type='email'
              name='email'
              inputMode='email'
              autoComplete='username'
              placeholder={t('forms.email')!}
              value={email}
              onChange={(event) => { setEmail(event.target.value); setError('') }}
              onBlur={() => setEmailBlurred(true)}
              onFocus={() => setEmailBlurred(false)}
              hasError={emailBlurred && !!emailError}
              isInputValid={isValidEmail(email)}
            />
            {emailBlurred && emailError && <ErrorText role='alert'>{emailError}</ErrorText>}
          </FormRow>

          <FormRow>
            <IconInput
              icon={<FaLock />}
              type='password'
              name='password'
              autoComplete={isRegistering ? 'new-password' : 'current-password'}
              placeholder={t('forms.password')!}
              value={password}
              onChange={(event) => { setPassword(event.target.value); setError('') }}
              onBlur={() => setPasswordBlurred(true)}
              onFocus={() => setPasswordBlurred(false)}
              hasError={passwordBlurred && !!passwordError}
              isInputValid={!passwordError}
            />
            {passwordBlurred && passwordError && <ErrorText role='alert'>{passwordError}</ErrorText>}
          </FormRow>

          {!isRegistering && (
            <ForgotPassword
              type='button'
              onClick={() => {
                logEvent('web_usc_openResetPasswordModal')
                setShowResetPasswordModal(true)
              }}
            >
              {t('forms.resetPassword')}
            </ForgotPassword>
          )}

          {isRegistering && (
            <FormRow>
              <IconInput
                icon={<FaUser />}
                type='text'
                name='firstName'
                autoCapitalize
                autoComplete='given-name'
                placeholder={t('forms.name')!}
                value={name}
                onChange={(event) => { setName(event.target.value); setError('') }}
                onBlur={() => setNameBlurred(true)}
                onFocus={() => setNameBlurred(false)}
                hasError={nameBlurred && !!nameError}
                isInputValid={!nameError}
                maxLength={MAX_NAME_LENGTH}
              />
              {nameBlurred && nameError && <ErrorText role='alert'>{nameError}</ErrorText>}
            </FormRow>
          )}

          <Notice>{t('usc.sameEmailNotice')}</Notice>

          {error && <ErrorText role='alert'>{error}</ErrorText>}

          {socialLoginAvailable && (
            <>
              <OrLineDivider />
              <SocialLogin
                onGooglePress={() => handleSocialSignIn('google')}
                onApplePress={() => handleSocialSignIn('apple')}
                disabled={loading}
              />
            </>
          )}

          <ActionBar>
            <Button type='submit' disabled={!isFormValid} margin='0'>
              {loading
                ? <ClipLoader size={20} color={theme.colors.basicWhite} speedMultiplier={1} />
                : t('usc.account.submit')}
            </Button>
          </ActionBar>
        </Form>
      </Card>

      {showResetPasswordModal && (
        <ResetPasswordModal
          closeModal={() => setShowResetPasswordModal(false)}
          sendResetPasswordEmail={handleResetPassword}
          email={trimmedEmail}
        />
      )}
    </Page>
  )
}

export default UscAccountContainer
